import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, Trash2, Plus, FileText, X, ShoppingCart } from 'lucide-react';
import '../../assets/css/cartpanel.css';

// CartPanel
// - Purely a UI/state layer on top of the SAME design (placementList /
//   placementTexts / placementImages never change here). It only lets the
//   customer pick which colorways + sizes/quantities they want printed
//   with that one design, mirroring the reference screenshots:
//     Cart
//       Option (Black/Castlerock) x1        <- "base" option, tied to the
//                                               color+sizes chosen back on
//                                               the Design tab
//       Option (Midnight Navy/Castlerock) x1 <- extra options added here
//       + Add More
//
// - "Add to cart" itself lives in CustomizerHeader (top action bar), this
//   component just prepares/exposes the option list + note + total qty.
const SIZE_LABELS = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

const emptySizes = () =>
  SIZE_LABELS.reduce(
    (acc, s) => ({
      ...acc,
      [s]: s === 'S' ? 1 : 0,
    }),
    {}
  );

const totalQtyOf = (sizes = {}) =>
  Object.values(sizes).reduce((sum, v) => sum + (Number(v) || 0), 0);

const CartPanel = ({
  productImage,
  productName = 'Custom Product',
  baseColor,
  setBaseColor,
  baseSizes,
  setBaseSizes,
  extraOptions = [],
  setExtraOptions = () => { },
  colors = [],
  pricePerUnit = 90.18,
  basePrice,
  designExtraFeePerUnit = 0,
  note = '',
  setNote = () => { },
  selectedColorHex,
  onSelectedColorHexChange = () => { },
  onAddToCart = () => { },
}) => {

  const [colorTables, setColorTables] = useState(() => {
    const obj = {};
    colors.forEach((color) => {
      obj[color.hex] = {
        sizes: {
          S: 0,
          M: 0,
          L: 0,
          XL: 0,
          "2XL": 0,
          "3XL": 0,
          "4XL": 0,
          "5XL": 0,
        },
        stocks: {
          S: 50,
          M: 540,
          L: 87,
          XL: 147,
          "2XL": 987,
          "3XL": 124,
          "4XL": 963,
          "5XL": 458,
        },
      };
    });

    return obj;
  });

  // Keep selection driving inputs, and ensure missing tables exist.
  const selectedColor = selectedColorHex || colors?.[0]?.hex;

  useEffect(() => {

    const ensureColorTable = (hex) => {
      if (!hex) return;
      setColorTables((prev) => {
        if (prev?.[hex]) return prev;
        const found = colors.find((c) => c.hex === hex);
        if (!found) return prev;

        return {
          ...prev,
          [hex]: {
            sizes: {
              S: 0,
              M: 0,
              L: 0,
              XL: 0,
              "2XL": 0,
              "3XL": 0,
              "4XL": 0,
              "5XL": 0,
            },
            stocks: {
              S: 50,
              M: 540,
              L: 87,
              XL: 147,
              "2XL": 987,
              "3XL": 124,
              "4XL": 963,
              "5XL": 458,
            },
          },
        };
      });
    };

    ensureColorTable(selectedColorHex);
  }, [])



  const PRICE_COLUMNS = [
    { label: "1 - 11", key: "p1" },
    { label: "12 - 35", key: "p2" },
    { label: "36 - 71", key: "p3" },
    { label: "72 - 143", key: "p4" },
    { label: "144 - MORE", key: "p5" },
  ];

  const pricing = {
    S: { stock: 3627, p1: 3.67, p2: 3.67, p3: 3.64, p4: 3.61, p5: 3.58 },
    M: { stock: 12166, p1: 3.67, p2: 3.67, p3: 3.64, p4: 3.61, p5: 3.58 },
    L: { stock: 12502, p1: 3.67, p2: 3.67, p3: 3.64, p4: 3.61, p5: 3.58 },
    XL: { stock: 11015, p1: 3.67, p2: 3.67, p3: 3.64, p4: 3.61, p5: 3.58 },
    "2XL": { stock: 3886, p1: 6.07, p2: 6.07, p3: 6.01, p4: 5.96, p5: 5.91 },
    "3XL": { stock: 1253, p1: 8.21, p2: 8.21, p3: 8.14, p4: 8.07, p5: 8.00 },
    "4XL": { stock: 289, p1: 8.21, p2: 8.21, p3: 8.14, p4: 8.07, p5: 8.00 },
    "5XL": { stock: 168, p1: 8.21, p2: 8.21, p3: 8.14, p4: 8.07, p5: 8.00 },
  };

  const updateQty = (size, value) => {
    const qty = Math.max(0, Number(value) || 0);

    setColorTables((prev) => ({
      ...prev,
      [selectedColor]: {
        ...prev[selectedColor],
        sizes: {
          ...prev[selectedColor].sizes,
          [size]: qty,
        },
      },
    }));
  };

  const handleQtyBlur = (size) => {
    const stock = pricing[size].stock;
    const currentQty = colorTables[selectedColor]?.sizes[size] || 0;

    if (currentQty > stock) {
      setColorTables((prev) => ({
        ...prev,
        [selectedColor]: {
          ...prev[selectedColor],
          sizes: {
            ...prev[selectedColor].sizes,
            [size]: stock,
          },
        },
      }));
    }
  };

  const clearAllQty = () => {
    setColorTables((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((hex) => {
        next[hex] = {
          ...next[hex],
          sizes: Object.keys(next[hex].sizes || {}).reduce((acc, s) => {
            acc[s] = 0;
            return acc;
          }, {}),
        };
      });
      return next;
    });
  };

  const overallQty = Object.values(colorTables).reduce((sum, color) => {
    return (
      sum +
      Object.values(color.sizes).reduce((a, b) => a + b, 0)
    );
  }, 0);


  const colorName = (hex) =>
    colors.find((c) => c.hex === hex)?.name || 'Custom';

  const allUsedColors = [baseColor, ...extraOptions.map((o) => o.color)];

  const allColorsUsed =
    colors.length > 0 && allUsedColors.length >= colors.length;


  const baseQty = totalQtyOf(baseSizes);
  // const overallQty =
  //   baseQty + extraOptions.reduce((sum, o) => sum + totalQtyOf(o.sizes), 0);

  // const renderSizeGrid = (sizes, onChange, disabledIfZeroPrice = false) => (
  //   <div className="cart-size-grid">
  //     {SIZE_LABELS.map((label) => (
  //       <div key={label} className="cart-size-cell">
  //         <div className="cart-size-label">{label}</div>
  //         <input
  //           type="number"
  //           min="0"
  //           className="cart-size-input"
  //           value={sizes?.[label] ?? 0}
  //           onChange={(e) => onChange(label, e.target.value)}
  //         />
  //         <div className="cart-size-price">
  //           ${((Number(sizes?.[label]) || 0) * pricePerUnit).toFixed(2)}
  //         </div>
  //       </div>
  //     ))}
  //   </div>
  // );

  const renderColorSwatches = (activeColor, onSelect, { disabledColors = [], locked = false } = {}) => (
    <div className="cart-color-row">
      {colors.map((c) => {
        const isActive = activeColor === c.hex;
        const isDisabled = locked || isActive || disabledColors.includes(c.hex);

        return (
          <button
            key={c.hex}
            type="button"
            className={`cart-color-swatch ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''
              }`}
            disabled={isDisabled}
            title={
              isActive
                ? `${c.name} is already selected`
                : locked
                  ? "Color is locked once you've added more options"
                  : isDisabled
                    ? `${c.name} is already used in another option`
                    : c.name
            }
            onClick={() => !isDisabled && onSelect(c.hex)}
          >
            <img src={c.image} alt={c.name} draggable={false} />
            <span className="cart-color-swatch-label">{c.name}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="cart-panel">
      <div className="cart-panel-header">
        <h3>Cart</h3>
      </div>

      <div className="cart-product-row">
        <div className="cart-product-thumb">
          <img src={productImage} alt={productName} />
        </div>
        <div className="cart-product-name">{productName}</div>
      </div>
      <div className="cart-colors">

        {colors.map((color) => (
          <button
            key={color.hex}
            className={`cart-color-btn ${selectedColor === color.hex ? "active" : ""}`}
            onClick={() => onSelectedColorHexChange(color.hex)}
          >
            <img src={color.image} alt={color.name} />
          </button>
        ))}
      </div>

      <table className="cart-pricing-table">
        <thead>
          <tr>
            <th>SIZE</th>
            <th>STOCK</th>

            {PRICE_COLUMNS.map((item) => (
              <th key={item.key}>{item.label}</th>
            ))}

            <th>QTY</th>
          </tr>
        </thead>

        <tbody>
          {SIZE_LABELS.map((size) => (
            <tr key={size}>
              <td>{size}</td>

              <td>{pricing[size].stock}</td>

              {PRICE_COLUMNS.map((item) => (
                <td key={item.key}>
                  ${pricing[size][item.key].toFixed(2)}
                </td>
              ))}

              <td>
                <input
                  type="number"
                  min="0"
                  value={colorTables[selectedColor]?.sizes[size] || 0}
                  onChange={(e) => updateQty(size, e.target.value)}
                  onBlur={() => handleQtyBlur(size)}
                  className="qty-input"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cart-price-note">
        <strong>*</strong> All prices shown are in Canadian Dollars (CAD).
      </div>
      <div className='cart-table-footer'>
        <div className="cart-total-quantity">
          Total Quantity (<strong>{overallQty}</strong>)
        </div>
        <button
          type="button"
          className="header-preview-button"
          onClick={() => {
            onAddToCart();
            clearAllQty();
          }}
        >
          <span>Add to cart</span>
          <ShoppingCart size={16} />
        </button>
      </div>
    </div>
  );
};

export default CartPanel;
export { SIZE_LABELS, emptySizes, totalQtyOf };
